/**
 * Locale-prefixed help-center URLs (#70).
 *
 * English lives at the bare, historically-indexed URLs (`/help/<slug>`); the
 * four translated locales get their own URL space scoped to the help center
 * (`/help/fr/<slug>`, …). The URL — not the UI language — is the source of
 * truth for which locale a help page serves, so every canonical URL always
 * serves the same content to every visitor (and to crawlers).
 *
 * Pure path helpers only — React hooks live in hooks/useHelpLocale.ts, and the
 * prerender/sitemap scripts mirror this scheme in scripts/prerender-lib.mjs and
 * scripts/sitemap-lib.mjs (keep the three in sync).
 */

/** Every locale the help center has content for. English first. */
export const HELP_CONTENT_LOCALES = ['en', 'fr', 'de', 'it', 'es'] as const;

/** Locales with a URL prefix — everything except English. */
export const HELP_URL_LOCALES = ['fr', 'de', 'it', 'es'] as const;

export type HelpContentLocale = (typeof HELP_CONTENT_LOCALES)[number];
export type HelpUrlLocale = (typeof HELP_URL_LOCALES)[number];

/** Native-language display names for the help language switcher. */
export const HELP_LOCALE_NAMES: Record<HelpContentLocale, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  es: 'Español',
};

const LOCALE_PREFIX_RE = new RegExp(
  `^/help/(${HELP_URL_LOCALES.join('|')})(?=/|$)`,
);

/**
 * Extract the locale prefix from a help pathname, if any.
 * `/help/fr/foo` -> 'fr'; `/help/foo` and non-help paths -> undefined.
 * No article slug collides with a locale code (2-letter codes, slugs are
 * multi-word) — guarded by a unit test against the real article set.
 */
export function parseHelpLocaleFromPath(
  pathname: string,
): HelpUrlLocale | undefined {
  const match = pathname.match(LOCALE_PREFIX_RE);
  return match ? (match[1] as HelpUrlLocale) : undefined;
}

/** Base path for building help links: '/help' for en, '/help/fr' for fr, … */
export function helpBasePath(locale?: string): string {
  return locale && locale !== 'en' ? `/help/${locale}` : '/help';
}

/**
 * Split a help pathname into its locale and the locale-independent suffix.
 * '/help/fr/category/account' -> {locale: 'fr', suffix: '/category/account'}
 * '/help/foo'                 -> {locale: 'en', suffix: '/foo'}
 * '/help'                     -> {locale: 'en', suffix: ''}
 */
export function splitHelpPath(pathname: string): {
  locale: HelpContentLocale;
  suffix: string;
} {
  const locale = parseHelpLocaleFromPath(pathname) ?? 'en';
  const suffix = pathname
    .replace(LOCALE_PREFIX_RE, '/help')
    .replace(/^\/help/, '')
    .replace(/\/+$/, '');
  return {locale, suffix};
}

/** Rebuild the current help path in another locale (used by the switcher). */
export function helpPathForLocale(
  pathname: string,
  targetLocale: string,
): string {
  const {suffix} = splitHelpPath(pathname);
  return `${helpBasePath(targetLocale)}${suffix}`;
}

/**
 * True for article pages (`/help/<slug>`, `/help/fr/<slug>`) — the layout
 * widens the container for the sticky table of contents. Landing and category
 * pages are not article views.
 */
export function isHelpArticlePath(pathname: string): boolean {
  const {suffix} = splitHelpPath(pathname);
  const segments = suffix.split('/').filter(Boolean);
  return segments.length === 1 && segments[0] !== 'category';
}
